import { useState } from 'react'
import { ShieldCheck, Drop } from '@phosphor-icons/react'
import { useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { Badge, Banner, Button, Field, Input, PhotoCapture, PickerSheet } from '@/ui'
import { ActionBar, BigFigure } from '../../components/GateScreen'
import { nextStep } from '../../model/delivery'
import {
  moistureFigure,
  needsThirdReading,
  parsePctTenths,
  readingInRange,
  tenthsToString,
} from '../../model/moisture'
import { useWizard } from './context'

const NONE = '__none__'

export function PctInput({
  value,
  onChange,
  label,
}: {
  value: string
  onChange: (v: string) => void
  label?: string
}) {
  return (
    <Input
      size="touch"
      inputMode="decimal"
      aria-label={label}
      className="font-mono tabular"
      value={value}
      onChange={(e) =>
        onChange(
          e.target.value
            .replace(',', '.')
            .replace(/[^\d.]/g, '')
            .slice(0, 5),
        )
      }
      trailing={<span className="font-mono">%</span>}
    />
  )
}

/** F4.4 — नमी जाँच · Moisture check. Required for groundnut; skipping it needs the supervisor's PIN. */
export function StepMoisture() {
  const { t } = useI18n()
  const { d, dispatch, roster, crop, stamp, requestSupervisor, startDispute } = useWizard()
  const [text, setText] = useState<string[]>(['', '', ''])
  const [photos, setPhotos] = useState<(string | null)[]>([null, null, null])
  const required = crop?.moisture_required ?? false
  const meters = roster.equipment.filter((e) => e.category === 'MOISTURE_METER')
  const cfg = roster.config
  const bags = d.moisture_bag_nos ?? []

  const vals = text.map((s) => (s.trim() === '' ? null : parsePctTenths(s)))
  const bad = vals.map(
    (v, i) =>
      text[i]!.trim() !== '' &&
      (v === null || !readingInRange(v, cfg.moisture_min_tenths, cfg.moisture_max_tenths)),
  )
  const third = needsThirdReading(vals[0] ?? null, vals[1] ?? null, cfg.moisture_spread_max_tenths)
  const used = third ? 3 : 2
  const photosRequired = d.reading_photos_required === true
  const canSave =
    !bad.slice(0, used).some(Boolean) &&
    vals.slice(0, used).every((v) => v !== null) &&
    (!photosRequired || photos.slice(0, used).every(Boolean))
  const figure = d.moisture.saved
    ? moistureFigure(d.moisture.readings)
    : canSave
      ? moistureFigure(vals.slice(0, used).map((v) => ({ tenths: v! })))
      : null
  const line = crop?.moisture_line_tenths ?? null
  const dryFirst = required && figure !== null && line !== null && figure > line

  const save = () => {
    if (!canSave) return
    const at = stamp()
    dispatch({
      type: 'save_moisture',
      readings: vals.slice(0, used).map((v, i) => ({
        id: newId(),
        tenths: v!,
        bag_no: i < 2 ? (bags[i] ?? null) : null,
        taken_at: at,
        chosen_by_farmer: false,
        photo_media_id: photos[i],
      })),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {required ? (
        <Banner tone="info">{t('gate.moisture.required')}</Banner>
      ) : (
        <Banner tone="info">{t('gate.moisture.optional')}</Banner>
      )}
      <Field label={t('gate.moisture.meter')}>
        <PickerSheet
          title={t('gate.moisture.meter')}
          value={d.moisture.equipment_id ?? (d.moisture.saved ? NONE : null)}
          disabled={d.moisture.saved}
          onChange={(v) => dispatch({ type: 'meter', equipment_id: v === NONE ? null : v })}
          options={[
            ...meters.map((m) => {
              const due = m.calibration_due_at?.slice(0, 10)
              const stale = !!due && due < d.started_at.slice(0, 10)
              return {
                value: m.id,
                label: m.name ?? m.code,
                meta: stale ? <Badge tone="warn">{t('gate.moisture.calStale')}</Badge> : undefined,
              }
            }),
            { value: NONE, label: t('gate.open.notRegistered') },
          ]}
        />
      </Field>

      {d.moisture.saved ? (
        <div className="flex flex-col gap-2">
          {d.moisture.readings.length === 0 ? (
            <Banner tone={d.moisture.skip_allowed_by ? 'warn' : 'info'}>
              {d.moisture.skip_allowed_by
                ? t('gate.moisture.skipped')
                : t('gate.moisture.noMeterSaved')}
            </Banner>
          ) : (
            <ul className="flex flex-col gap-1">
              {d.moisture.readings.map((r, i) => (
                <li
                  key={r.id}
                  className="flex justify-between rounded-chip bg-paper-sunk px-3 py-2 font-mono text-[15px]"
                >
                  <span>
                    {t('gate.moisture.readingN', { n: i + 1 })}
                    {r.bag_no ? ` · ${t('gate.moisture.fromBag', { n: r.bag_no })}` : ''}
                    {r.chosen_by_farmer ? ` · ${t('gate.moisture.farmerPicked')}` : ''}
                  </span>
                  <span>{tenthsToString(r.tenths)}%</span>
                </li>
              ))}
            </ul>
          )}
          {d.moisture.readings.length > 0 && (
            <Button
              variant="secondary"
              size="touch"
              onClick={() => startDispute({ about: 'MOISTURE' })}
            >
              {t('gate.dispute.button')}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].slice(0, used).map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <Field
                label={t('gate.moisture.readingN', { n: i + 1 })}
                required
                hint={
                  i < 2 && bags[i]
                    ? t('gate.moisture.fromBag', { n: bags[i]! })
                    : i === 2
                      ? t('gate.moisture.thirdHint')
                      : undefined
                }
                error={bad[i] ? t('gate.moisture.retype') : undefined}
              >
                <PctInput
                  value={text[i]!}
                  onChange={(v) => setText((x) => x.map((y, j) => (j === i ? v : y)))}
                />
              </Field>
              {d.capture_mode === 'APP' && (
                <Field
                  label={`${t('gate.v1.moisture.photo')} · ${t('gate.moisture.readingN', { n: i + 1 })}`}
                  required={photosRequired}
                >
                  <PhotoCapture
                    value={photos[i] ?? null}
                    onChange={(photo) =>
                      setPhotos((current) => current.map((p, j) => (j === i ? photo : p)))
                    }
                  />
                </Field>
              )}
            </div>
          ))}
          {third && <Banner tone="warn">{t('gate.moisture.needThird')}</Banner>}
        </div>
      )}

      {figure !== null && (
        <div className="grid grid-cols-2 gap-2">
          <BigFigure label={t('gate.moisture.figure')} value={`${tenthsToString(figure)}%`} />
          {dryFirst && (
            <div className="flex items-center gap-2 rounded-card border border-warn/30 bg-warn-wash px-3 text-[14px] font-semibold text-warn">
              <Drop aria-hidden="true" /> {t('gate.moisture.dryFirst')}
            </div>
          )}
        </div>
      )}

      <ActionBar>
        {d.moisture.saved ? (
          <Button
            size="xl"
            fullWidth
            onClick={() => dispatch({ type: 'goto', step: nextStep(d, 'moisture') })}
          >
            {t('common.next')}
          </Button>
        ) : (
          <>
            <Button size="xl" fullWidth disabled={!canSave} onClick={save}>
              {t('common.save')}
            </Button>
            {!required ? (
              <Button
                size="touch"
                variant="ghost"
                fullWidth
                onClick={() => dispatch({ type: 'no_meter' })}
              >
                {t('gate.moisture.noMeter')}
              </Button>
            ) : (
              <Button
                size="touch"
                variant="ghost"
                fullWidth
                leadingIcon={<ShieldCheck aria-hidden="true" />}
                onClick={async () => {
                  const s = await requestSupervisor(t('gate.moisture.supReason'))
                  if (s) dispatch({ type: 'moisture_skip', by: s.user_id })
                }}
              >
                {t('gate.moisture.supAllows')}
              </Button>
            )}
          </>
        )}
      </ActionBar>
    </div>
  )
}
